import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { EsraPipingAPIClient, ValveViewModel, SearchResponse_1OfValveViewModel, SearchRequest } from 'src/app/shared/models/autogenerated-piping';
import { indicate } from 'src/app/operators';
import { TechnologyEnum } from 'src/app/shared/models/technology-enum';

export interface ValveCatalogSelectionData {
  selectedValves?: ValveViewModel[];
  existingValveIds?: string[];
  title?: string;
}

@Component({
  selector: 'app-valve-catalog-modal',
  templateUrl: './valve-catalog-modal.component.html',
  styleUrls: ['./valve-catalog-modal.component.scss'],
  standalone: false
})
export class ValveCatalogModalComponent implements OnInit {
  displayedColumns: string[] = ['select', 'tag', 'shortDescription', 'nps', 'rating', 'ends', 'description'];
  dataSource = new MatTableDataSource<ValveViewModel>([]);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  pageSize = 10;
  pageIndex = 0; // zero-based for paginator; API is 1-based
  totalRecords = 0;
  isLoading$ = new BehaviorSubject<boolean>(false);

  selectedValves: ValveViewModel[] = [];
  existingValveIds: string[] = [];
  modalTitle = 'Select Valves';
  searchTerm = '';
  currentPage = 0;
  technology: TechnologyEnum = TechnologyEnum.PIPES;
  currentSearchRequest: SearchRequest;
  searching$ = new BehaviorSubject<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<ValveCatalogModalComponent>,
    private snackbarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: ValveCatalogSelectionData,
    private api: EsraPipingAPIClient
  ) {
    this.selectedValves = data?.selectedValves ? [...data.selectedValves] : [];
    this.existingValveIds = data?.existingValveIds ? [...data.existingValveIds] : [];
    this.modalTitle = data?.title || 'Select Valves';
  }

  ngOnInit(): void {
    this.loadPage();
  }

  loadPage(): void {
    const indicator = indicate(this.isLoading$);
    // API page number is 1-based
    this.api.getValvesByPageNumberAndNumberOfResults(this.pageIndex + 1, this.pageSize)
      .pipe(
        first(),
        indicator,
        tap((resp: SearchResponse_1OfValveViewModel) => {
          this.totalRecords = resp.totalResults || 0;
          let results = resp.results || [];
          if (this.searchTerm && this.searchTerm.trim().length > 0) {
            const term = this.searchTerm.toLowerCase();
            // client side filter (API lacks search param): filter by tag, description, shortDescription
            results = results.filter(v =>
              (v.tag || '').toLowerCase().includes(term) ||
              (v.description || '').toLowerCase().includes(term) ||
              (v.shortDescription || '').toLowerCase().includes(term)
            );
          }
          // Preserve selection flags
            results.forEach(v => {
              (v as any).__selected = this.selectedValves.some(s => s.id === v.id);
            });
          this.dataSource.data = results;
        })
      )
      .subscribe({
        error: err => console.error('Error loading valves', err)
      });
  }

  onSearch(): void {
    this.pageIndex = 0;
    this.loadPage();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPage();
  }

  

   onArchiveValve(valveId: string): void {
    
    console.log('In Archive Valve function');
    if (!valveId) {
      this.snackbarService.showSnackBar(true, 'Unable to archive: valve id is missing');
      return;
    }
  
    this.api.archiveValve(valveId)
      .pipe(
        indicate(this.searching$),
        first()
      )
      .subscribe({
        next: () => {
          this.snackbarService.showSnackBar(true, 'Valve archived successfully');
  
          this.selectedValves = this.selectedValves.filter(v => v.id !== valveId);
  
          this.loadPage();
        },
        error: (err) => {
          const msg =
            err?.response ??
            err?.error?.message ??
            err?.message ??
            'Failed to archive valve';
          this.snackbarService.showSnackBar(true, msg);
        },
  
      });
    }
    

  onArchiveClick(valve: ValveViewModel) {
    const valveId = valve.id || '';
    this.onArchiveValve(valveId);
  }


  isValveSelected(valve: ValveViewModel): boolean {
    return this.selectedValves.some(v => v.id === valve.id);
  }



  isExistingValve(valve: ValveViewModel): boolean {
    return this.existingValveIds.includes(valve.id || '');
  }

  toggleValveSelection(valve: ValveViewModel): void {
    if (this.isExistingValve(valve)) {
      return; // Do not allow selecting existing valves
    }
    const idx = this.selectedValves.findIndex(v => v.id === valve.id);
    if (idx >= 0) {
      this.selectedValves.splice(idx, 1);
      (valve as any).__selected = false;
    } else {
      this.selectedValves.push(valve);
      (valve as any).__selected = true;
    }
  }

  selectAll(): void {
    this.dataSource.data.forEach(v => {
      if (!this.isExistingValve(v) && !this.isValveSelected(v)) {
        this.selectedValves.push(v);
        (v as any).__selected = true;
      }
    });
  }

  clearAll(): void {
    // Only clear those in current page to avoid wiping earlier page selections? We'll clear all to simplify.
    this.selectedValves = [];
    this.dataSource.data.forEach(v => (v as any).__selected = false);
  }

  confirm(): void {
    this.dialogRef.close(this.selectedValves);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
